import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "../popupsright.css";
import firecorrect from "../../images/firecorrect.png";

const toLocalISODate = (dateValue) => {
  const date = new Date(dateValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const normalizeDbDay = (value) => String(value || "").slice(0, 10);

const getWeekdayLabel = (dateValue) => {
  const labels = ["D", "L", "Ma", "Mi", "J", "V", "S"];
  return labels[new Date(dateValue).getDay()] || "";
};

const FlameButtonWithDropdown = () => {
  const btnRef = useRef(null);
  const popupRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL || "";
  const streakTargetDays = 7;

  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [openedDayKey, setOpenedDayKey] = useState(null);

  const [streakInfo, setStreakInfo] = useState({
    isLoading: false,
    currentStreak: 0,
    todayXp: 0,
    todayCompleted: false,
    weekDays: [],
  });

  const fetchStreakData = useCallback(async () => {
    setStreakInfo((previous) => ({
      ...previous,
      isLoading: true,
    }));

    try {
      const response = await fetch(`${apiUrl}/daily-lessons`, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Nu s-au putut incarca datele de streak.");
      }

      const payload = await response.json();

      const activityByDay = new Map();

      const addActivity = (row) => {
        const key = normalizeDbDay(row?.day);

        if (!key) return;

        activityByDay.set(key, {
          xp: Number(row?.daily_xp || 0),
          lessonsCompleted: Number(row?.lessons_completed || 0),
        });
      };

      addActivity(payload?.today);

      if (Array.isArray(payload?.history)) {
        payload.history.forEach(addActivity);
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayKey = toLocalISODate(today);

      const todayActivity = activityByDay.get(todayKey);

      const todayCompleted =
        Number(todayActivity?.lessonsCompleted || 0) > 0;

      const todayXp =
        Number(todayActivity?.xp || 0);

      /*
       * Daca azi exista activitate, streak-ul incepe de azi.
       *
       * Daca azi NU exista activitate, verificam de ieri.
       * Astfel seria nu dispare la ora 00:00.
       */
      const cursor = new Date(today);

      if (!todayCompleted) {
        cursor.setDate(cursor.getDate() - 1);
      }

      let currentStreak = 0;

      while (true) {
        const cursorKey = toLocalISODate(cursor);
        const activity = activityByDay.get(cursorKey);

        const completed =
          Number(activity?.lessonsCompleted || 0) > 0;

        if (!completed) {
          break;
        }

        currentStreak += 1;
        cursor.setDate(cursor.getDate() - 1);
      }

      /*
       * Afisam mereu ultimele 7 zile pana azi.
       *
       * Astfel, chiar daca streak-ul ajunge la 30 zile,
       * componenta vizuala ramane stabila.
       */
      const displayStart = new Date(today);
      displayStart.setDate(today.getDate() - 6);
      displayStart.setHours(0, 0, 0, 0);

      const weekDays = Array.from({ length: 7 }).map((_, index) => {
        const date = new Date(displayStart);

        date.setDate(displayStart.getDate() + index);
        date.setHours(0, 0, 0, 0);

        const dayKey = toLocalISODate(date);

        const activity = activityByDay.get(dayKey);

        const xp = Number(activity?.xp || 0);
        const lessonsCompleted =
          Number(activity?.lessonsCompleted || 0);

        const completed = lessonsCompleted > 0;
        const isCurrentDay = dayKey === todayKey;

        return {
          key: dayKey,
          label: getWeekdayLabel(date),
          xp,
          lessonsCompleted,
          completed,
          isCurrentDay,
        };
      });

      setStreakInfo({
        isLoading: false,
        currentStreak,
        todayXp,
        todayCompleted,
        weekDays,
      });
    } catch (error) {
      console.error("Streak fetch error:", error);

      setStreakInfo((previous) => ({
        ...previous,
        isLoading: false,
      }));
    }
  }, [apiUrl]);

  const openPopup = () => {
    if (isVisible && isOpen) return;

    setIsVisible(true);

    requestAnimationFrame(() => {
      setIsOpen(true);
    });
  };

  const closePopup = () => {
    if (!isVisible) return;

    setIsOpen(false);
  };

  useEffect(() => {
    const resize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  /*
   * Incarca streak-ul cand componenta este montata.
   */
  useEffect(() => {
    fetchStreakData();
  }, [fetchStreakData]);

  /*
   * Reincarca datele de fiecare data cand popup-ul este deschis.
   */
  useEffect(() => {
    if (!isVisible) return;

    fetchStreakData();
    setOpenedDayKey(null);
  }, [isVisible, fetchStreakData]);

  /*
   * Daca utilizatorul revine in tab dupa ce a terminat o lectie,
   * reincarcam datele.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchStreakData();
      }
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [fetchStreakData]);

  /*
   * Reincarca datele cand fereastra primeste din nou focus.
   */
  useEffect(() => {
    const handleFocus = () => {
      fetchStreakData();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchStreakData]);

  /*
   * Inchide popup-ul la click in afara.
   */
  useEffect(() => {
    const close = (event) => {
      if (
        isVisible &&
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        !btnRef.current?.contains(event.target)
      ) {
        closePopup();
      }
    };

    document.addEventListener("mousedown", close);

    return () => {
      document.removeEventListener("mousedown", close);
    };
  }, [isVisible]);

  /*
   * Scoate popup-ul din DOM dupa animatie.
   */
  useEffect(() => {
    if (!isVisible) return;

    const closeTimer = setTimeout(() => {
      if (!isOpen) {
        setIsVisible(false);
      }
    }, 280);

    return () => {
      clearTimeout(closeTimer);
    };
  }, [isVisible, isOpen]);

  /*
   * Clasa aplicata pe body cat timp popup-ul este deschis.
   */
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("stats-popup-open");
    } else {
      document.body.classList.remove("stats-popup-open");
    }

    return () => {
      document.body.classList.remove("stats-popup-open");
    };
  }, [isOpen]);

  /*
   * ESC inchide popup-ul.
   */
  useEffect(() => {
    if (!isVisible) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closePopup();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isVisible]);

  /*
   * Inchide XP popover daca utilizatorul apasa in alta parte.
   */
  useEffect(() => {
    if (!isVisible || !openedDayKey) return;

    const handleOutsideDayClick = (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (target.closest(".streak-day-wrap")) {
        return;
      }

      setOpenedDayKey(null);
    };

    document.addEventListener(
      "mousedown",
      handleOutsideDayClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideDayClick
      );
    };
  }, [isVisible, openedDayKey]);

  const streakProgressPercent = Math.min(
    100,
    Math.round(
      (streakInfo.currentStreak / streakTargetDays) * 100
    )
  );

  const streakTitle = `${streakInfo.currentStreak} ${
    streakInfo.currentStreak === 1
      ? "zi consecutiva"
      : "zile consecutive"
  }`;

  let streakSubtitle;

  if (streakInfo.todayCompleted) {
    streakSubtitle = streakInfo.todayXp > 0
      ? `Ai ${streakInfo.todayXp} XP azi. Continua seria!`
      : "Ai finalizat o lectie azi. Continua seria!";
  } else if (streakInfo.currentStreak > 0) {
    streakSubtitle =
      "Finalizeaza o lectie azi pentru a continua seria!";
  } else {
    streakSubtitle =
      "Finalizeaza o lectie azi pentru a incepe un nou streak!";
  }

  const remainingDays = Math.max(
    streakTargetDays - streakInfo.currentStreak,
    0
  );

  const clubDescription =
    remainingDays === 0
      ? "Seria este activa. Continua zilnic ca sa o mentii!"
      : `Finalizeaza inca ${remainingDays} ${
          remainingDays === 1 ? "zi" : "zile"
        } consecutive de studiu pentru a mentine seria.`;

  return (
    <div className="first-main-pagi-flame-wrapper">
      <div
        ref={btnRef}
        className="first-main-pagi-stats-button"
        onClick={isOpen ? closePopup : openPopup}
      >
        <img
          src={firecorrect}
          alt="flame"
          className="first-main-pagi-stats-icon"
        />

        <div className="first-main-pagi-stats-number">
          {streakInfo.currentStreak}
        </div>
      </div>

      {isVisible &&
        createPortal(
          <>
            <div
              className={`first-main-pagi-overlay first-main-pagi-flame-overlay ${
                isOpen ? "open" : ""
              }`}
              onClick={closePopup}
            />

            <div
              ref={popupRef}
              className={`first-main-pagi-popup first-main-pagi-flame-popup first-main-pagi-flame-popup-anim ${
                isOpen ? "open" : ""
              } ${isMobile ? "mobile" : "desktop"}`}
            >
              <div className="streak-popup-header">
                <div className="streak-title">
                  {streakTitle}
                </div>

                <div className="streak-subtitle">
                  {streakInfo.isLoading
                    ? "Se incarca datele de streak..."
                    : streakSubtitle}
                </div>

                <div className="streak-flame-icon">
                  <span
                    role="img"
                    aria-label="flame"
                    style={{ fontSize: "2rem" }}
                  >
                    <img
                      src={firecorrect}
                      alt="flame"
                      className="first-main-pagi-stats-icon"
                    />
                  </span>
                </div>
              </div>

              <div className="streak-days-row">
                {streakInfo.weekDays.map((dayItem) => (
                  <div
                    className="streak-day-wrap"
                    key={dayItem.key}
                  >
                    <button
                      type="button"
                      className={`streak-day ${
                        dayItem.isCurrentDay ? "active" : ""
                      } ${
                        dayItem.completed ? "completed" : ""
                      }`.trim()}
                      onClick={() => {
                        setOpenedDayKey((previousKey) =>
                          previousKey === dayItem.key
                            ? null
                            : dayItem.key
                        );
                      }}
                      aria-label={`XP pentru ${dayItem.label}`}
                      aria-expanded={
                        openedDayKey === dayItem.key
                      }
                    >
                      {dayItem.label}
                    </button>

                    {dayItem.isCurrentDay ? (
                      <span
                        className="streak-day-indicator streak-day-indicator-flame"
                        aria-label="Ziua curenta"
                      >
                        <img
                          src={firecorrect}
                          alt="flame"
                          className="streak-day-indicator-flame-icon"
                        />
                      </span>
                    ) : dayItem.completed ? (
                      <span
                        className="streak-day-indicator streak-day-indicator-check"
                        aria-label="Zi completata"
                      >
                        ✓
                      </span>
                    ) : (
                      <span
                        className="streak-day-indicator streak-day-indicator-empty"
                        aria-label="Zi necompletata"
                      >
                        ○
                      </span>
                    )}

                    {openedDayKey === dayItem.key && (
                      <div
                        className="streak-day-xp-popover"
                        role="status"
                        aria-live="polite"
                      >
                        {dayItem.xp} XP
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="streak-club-box">
                <div className="streak-club-lock">
                  <span
                    role="img"
                    aria-label="lock"
                    style={{
                      fontSize: "1.5rem",
                      marginRight: 8,
                    }}
                  />

                  <span className="streak-club-title">
                    Mentine seria
                  </span>
                </div>

                <div className="streak-club-desc">
                  {clubDescription}
                </div>

                <div className="streak-progress-bar">
                  <div
                    className="streak-progress"
                    style={{
                      width: `${streakProgressPercent}%`,
                    }}
                  />

                  <span
                    className="streak-progress-flame"
                    style={{
                      left: `clamp(
                        12px,
                        ${streakProgressPercent}%,
                        calc(100% - 12px)
                      )`,
                    }}
                  >
                    <img
                      src={firecorrect}
                      alt="flame"
                      className="first-main-pagi-stats-icon"
                    />
                  </span>
                </div>
              </div>

              <button
                className="streak-view-more-btn"
                onClick={closePopup}
              >
                <span className="color-button-streak">
                  Am inteles
                </span>
              </button>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default FlameButtonWithDropdown;